// /induction — The Digital Handshake · Founder Node onboarding
// ================================================================
// Standalone page. NOT a VerticalCockpit wrapper. Dedicated to
// signing the 7-Hour Handshake and activating the Founder Node.
//
// Aesthetic: Grit & Gear · warm parchment + walnut ink + honey
// amber gold. Reads like signing a real contract at a trusted
// advisor's desk. Light background (deliberately inverted from
// the vertical cockpits) so the moment feels ceremonial, not
// tactical.
// ================================================================
import { useState } from "react";
import { Link } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import {
  ShieldCheck,
  ArrowRight,
  ArrowLeft,
  Trophy,
  Lock,
  Check,
  Feather,
  CheckCircle2,
} from "lucide-react";
import { VIDEOS } from "@/config/AssetSpine";
import { Logo } from "@/components/Logo";

// Warm wood / parchment palette — bespoke to /induction.
// Not exported to AssetSpine because this skin is intentionally
// single-use (contract-signing moment, not a vertical demo).
const P = {
  BG:         "#F3E6CE",   // parchment cream
  BG_HI:      "#E8D6B4",   // aged paper
  BG_DARK:    "#DBC496",   // dark parchment (elevated card fills)
  INK:        "#241606",   // walnut ink
  INK_SOFT:   "#4A2E14",   // brown ink secondary
  MUTED:      "#7A5C3A",   // aged sepia
  GOLD:       "#B8860B",   // weathered gold
  GOLD_HI:    "#E8B04D",   // honey amber highlight
  GOLD_DEEP:  "#7A5A08",   // deep bourbon
  BORDER:     "#C9A97A",   // aged brass
  LEATHER:    "#4A2E1E",   // deep leather
  RUST:       "#8B4513",   // saddle rust
};

const STRIPE_PRODUCT_ID = "prod_V0LizBaJsguToc";

const PRICING_LADDER = [
  {
    step: "Month 1",
    tag: "Today",
    amount: "$0",
    label: "Release Phase",
    body:
      "Agent-assisted setup. Amanda goes live on your line the same week. Zero risk, full activation.",
  },
  {
    step: "Month 2",
    tag: "Pilot",
    amount: "$97",
    label: "Pilot Phase",
    body:
      "Your system goes live. First real customers. Every module — Booking, Calendar, Billing, Notes, Ads — running end-to-end.",
  },
  {
    step: "Year 1",
    tag: "Locked",
    amount: "$297/mo",
    label: "Full Utility Lock",
    body:
      "Rest of Year 1 onward. Founder rate locked for life. New Founder Nodes after Q1 2026 pay the full going rate.",
  },
];

export default function Induction() {
  const [accepted, setAccepted] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [searchParams] = useSearchParams();
  const vertical = searchParams.get("vertical") || "";
  const returnPath = vertical ? `/${vertical}` : "/";

  // -------- SUCCESS STATE --------
  // Rendered instead of the induction form once the Founder Node is
  // activated. Deliberately does NOT redirect — user stays on
  // /induction until they tap ENTER YOUR COMMAND CENTER, which
  // routes them back to the vertical they came from (or home).
  if (completed) {
    return <SuccessView P={P} returnPath={returnPath} vertical={vertical} />;
  }

  return (
    <div
      className="min-h-screen relative"
      style={{
        background: P.BG,
        color: P.INK,
        // Warm paper grain — very subtle diagonal fibre.
        backgroundImage: `
          radial-gradient(circle at 15% 20%, ${P.GOLD}0F 0.5px, transparent 1.2px),
          radial-gradient(circle at 75% 50%, ${P.LEATHER}0D 0.5px, transparent 1.2px),
          repeating-linear-gradient(115deg, transparent 0 22px, ${P.BORDER}09 22px 23px)
        `,
        backgroundSize: "34px 34px, 46px 46px, 100% 100%",
      }}
    >
      {/* Header — parchment nav */}
      <header
        className="relative z-10 border-b"
        style={{
          borderColor: `${P.BORDER}66`,
          background: `linear-gradient(180deg, ${P.BG_HI} 0%, ${P.BG} 100%)`,
        }}
      >
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-5 flex items-center justify-between">
          <Link
            to="/"
            data-testid="ind-nav-home"
            className="inline-flex items-center gap-3 group"
          >
            <Logo />
            <div className="hidden sm:block">
              <div
                className="text-[10px] uppercase tracking-[0.28em] font-mono"
                style={{ color: P.MUTED }}
              >
                Wingman
              </div>
              <div
                className="text-sm font-display tracking-wide"
                style={{ color: P.INK }}
              >
                Alfred · Academy
              </div>
            </div>
          </Link>

          <div
            className="text-[10px] uppercase tracking-[0.22em] font-mono flex items-center gap-2 px-3 py-2 rounded-sm border"
            style={{
              color: P.GOLD_DEEP,
              borderColor: `${P.GOLD}66`,
              background: `${P.GOLD}14`,
            }}
          >
            <Lock className="w-3.5 h-3.5" />
            Founder Induction · Private
          </div>
        </div>
      </header>

      {/* SECTION 0 — Amanda hero video */}
      <section className="relative">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 pt-10 sm:pt-14">
          <div
            className="text-[11px] uppercase tracking-[0.28em] font-mono mb-4 flex items-center gap-2"
            style={{ color: P.GOLD_DEEP }}
          >
            <Feather className="w-3.5 h-3.5" />
            <span>The Digital Handshake · Amanda welcomes you</span>
          </div>

          <div
            className="rounded-sm overflow-hidden border-2"
            style={{
              borderColor: P.BORDER,
              boxShadow: `0 24px 60px -24px ${P.LEATHER}55, inset 0 1px 0 ${P.GOLD_HI}44`,
            }}
            data-testid="ind-hero-video-frame"
          >
            <video
              src={VIDEOS.AMANDA_FOUNDER}
              autoPlay
              muted
              loop
              playsInline
              controls
              preload="metadata"
              className="w-full aspect-video bg-black"
              data-testid="ind-hero-video"
            />
          </div>
        </div>
      </section>

      {/* SECTION 1 — Digital Handshake */}
      <section
        className="relative mt-14 sm:mt-20"
        data-testid="ind-section-handshake"
      >
        <div className="max-w-4xl mx-auto px-6 sm:px-8">
          <div
            className="text-[11px] uppercase tracking-[0.28em] font-mono mb-3"
            style={{ color: P.GOLD_DEEP }}
          >
            Step 1 · Accountability
          </div>

          <h1
            className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mb-6"
            style={{ color: P.INK }}
            data-testid="ind-section-1-heading"
          >
            The Digital Handshake — Step 1: Accountability.
          </h1>

          {/* The Agreement · document-style card */}
          <div
            className="rounded-sm border-2 p-8 sm:p-10 relative"
            style={{
              background: `linear-gradient(180deg, ${P.BG_HI} 0%, ${P.BG} 100%)`,
              borderColor: P.BORDER,
              boxShadow: `0 30px 60px -30px ${P.LEATHER}66, inset 0 1px 0 ${P.GOLD_HI}22`,
            }}
          >
            {/* Deed-style corner glyphs */}
            <div
              aria-hidden
              className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.28em] font-mono"
              style={{ color: `${P.MUTED}` }}
            >
              Ohio Digital Grid · Article I
            </div>
            <div
              aria-hidden
              className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.28em] font-mono"
              style={{ color: `${P.MUTED}` }}
            >
              Founder Node · Doctrine
            </div>

            <p
              className="font-serif text-lg sm:text-xl leading-[1.7] mt-6"
              style={{ color: P.INK_SOFT }}
              data-testid="ind-agreement-body"
            >
              The Ohio Digital Grid isn&apos;t software; it&apos;s a
              commitment. By checking the box below, you commit to{" "}
              <span
                className="font-semibold italic"
                style={{ color: P.LEATHER }}
              >
                7 hours of &ldquo;Future Activation&rdquo;
              </span>{" "}
              every week — working{" "}
              <span
                className="italic"
                style={{ color: P.LEATHER }}
              >
                on
              </span>{" "}
              your business systems with Amanda.
            </p>

            {/* Signature line divider */}
            <div
              className="my-8 flex items-center gap-4"
              aria-hidden
            >
              <div
                className="flex-1 h-px"
                style={{ background: `linear-gradient(90deg, transparent 0%, ${P.BORDER} 50%, transparent 100%)` }}
              />
              <div
                className="text-[9px] uppercase tracking-[0.32em] font-mono"
                style={{ color: P.MUTED }}
              >
                Signature Line
              </div>
              <div
                className="flex-1 h-px"
                style={{ background: `linear-gradient(90deg, transparent 0%, ${P.BORDER} 50%, transparent 100%)` }}
              />
            </div>

            {/* Checkbox · large, real, click-anywhere */}
            <label
              className="flex items-start gap-4 cursor-pointer group select-none"
              data-testid="ind-handshake-checkbox-label"
            >
              <span
                className="mt-0.5 flex-shrink-0 w-7 h-7 rounded-sm border-2 grid place-items-center transition-all"
                style={{
                  borderColor: accepted ? P.GOLD : P.BORDER,
                  background: accepted ? P.GOLD : `${P.BG}CC`,
                  boxShadow: accepted
                    ? `0 0 24px -6px ${P.GOLD}, inset 0 1px 0 ${P.GOLD_HI}`
                    : `inset 0 1px 2px ${P.LEATHER}22`,
                }}
              >
                {accepted && (
                  <Check
                    className="w-4 h-4"
                    style={{ color: P.BG }}
                    strokeWidth={3}
                  />
                )}
              </span>

              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="sr-only"
                data-testid="ind-handshake-checkbox"
              />

              <span
                className="font-serif text-base sm:text-lg leading-[1.6] pt-0.5"
                style={{ color: accepted ? P.LEATHER : P.INK_SOFT }}
              >
                I accept the 7-Hour Handshake and the Grid Operating
                Doctrine.
              </span>
            </label>

            {/* Handshake stamp — shows when signed */}
            {accepted && (
              <div
                className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-sm border font-mono text-[10px] uppercase tracking-[0.28em]"
                style={{
                  borderColor: P.GOLD,
                  background: `${P.GOLD}18`,
                  color: P.GOLD_DEEP,
                }}
                data-testid="ind-handshake-stamp"
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                Signed · {new Date().toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" })}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* SECTION 2 — Utility Registration · Stripe module */}
      <section
        className="relative mt-16 sm:mt-24 pb-24"
        data-testid="ind-section-stripe"
      >
        <div className="max-w-4xl mx-auto px-6 sm:px-8">
          <div
            className="text-[11px] uppercase tracking-[0.28em] font-mono mb-3"
            style={{ color: P.GOLD_DEEP }}
          >
            Step 2 · Utility Registration
          </div>

          <h2
            className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mb-8"
            style={{ color: P.INK }}
            data-testid="ind-section-2-heading"
          >
            Step 2: Utility Registration.
          </h2>

          {/* Product card */}
          <div
            className="rounded-sm border-2 overflow-hidden"
            style={{
              background: `linear-gradient(180deg, ${P.BG_HI} 0%, ${P.BG_DARK} 100%)`,
              borderColor: P.BORDER,
              boxShadow: `0 30px 60px -30px ${P.LEATHER}66, inset 0 1px 0 ${P.GOLD_HI}33`,
            }}
          >
            {/* Product header */}
            <div
              className="px-6 sm:px-8 py-5 border-b flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
              style={{
                borderColor: `${P.BORDER}88`,
                background: `linear-gradient(90deg, ${P.LEATHER}12 0%, transparent 100%)`,
              }}
            >
              <div>
                <div
                  className="text-[10px] uppercase tracking-[0.28em] font-mono mb-1"
                  style={{ color: P.MUTED }}
                >
                  Product · Stripe
                </div>
                <div
                  className="font-serif text-xl sm:text-2xl leading-tight"
                  style={{ color: P.INK }}
                  data-testid="ind-product-name"
                >
                  Ohio Digital Grid — Founder Node
                </div>
              </div>
              <div
                className="text-[10px] uppercase tracking-[0.24em] font-mono px-3 py-1.5 rounded-sm border inline-flex items-center gap-2 self-start"
                style={{
                  borderColor: `${P.MUTED}66`,
                  background: `${P.BG}88`,
                  color: P.MUTED,
                }}
                data-testid="ind-product-id"
              >
                <Lock className="w-3 h-3" />
                {STRIPE_PRODUCT_ID}
              </div>
            </div>

            {/* 1-2-12 pricing ladder */}
            <div className="grid grid-cols-1 md:grid-cols-3">
              {PRICING_LADDER.map((rung, i) => (
                <div
                  key={rung.step}
                  className="p-6 sm:p-8 border-b md:border-b-0 md:border-r last:border-r-0"
                  style={{ borderColor: `${P.BORDER}55` }}
                  data-testid={`ind-price-rung-${i}`}
                >
                  <div className="flex items-baseline justify-between mb-3">
                    <div
                      className="text-[10px] uppercase tracking-[0.28em] font-mono"
                      style={{ color: P.GOLD_DEEP }}
                    >
                      {rung.step}
                    </div>
                    <div
                      className="text-[9px] uppercase tracking-[0.28em] font-mono px-2 py-0.5 rounded-sm"
                      style={{
                        background: i === 0 ? `${P.GOLD}22` : `${P.LEATHER}11`,
                        color: i === 0 ? P.GOLD_DEEP : P.MUTED,
                      }}
                    >
                      {rung.tag}
                    </div>
                  </div>
                  <div
                    className="font-serif text-4xl sm:text-5xl leading-none mb-2"
                    style={{ color: P.INK }}
                  >
                    {rung.amount}
                  </div>
                  <div
                    className="text-[13px] font-semibold mb-2"
                    style={{ color: P.LEATHER }}
                  >
                    {rung.label}
                  </div>
                  <div
                    className="text-[13px] leading-[1.55]"
                    style={{ color: P.INK_SOFT }}
                  >
                    {rung.body}
                  </div>
                </div>
              ))}
            </div>

            {/* CTA row */}
            <div
              className="p-6 sm:p-8 border-t"
              style={{
                borderColor: `${P.BORDER}88`,
                background: `linear-gradient(180deg, ${P.BG_DARK} 0%, ${P.BG_HI} 100%)`,
              }}
            >
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-5">
                <button
                  type="button"
                  disabled={!accepted}
                  onClick={() => setCompleted(true)}
                  data-testid="ind-activate-cta"
                  className="flex-1 inline-flex items-center justify-center gap-3 px-8 py-5 rounded-sm font-display uppercase tracking-[0.22em] text-sm transition-all"
                  style={{
                    background: accepted
                      ? `linear-gradient(90deg, ${P.GOLD_DEEP} 0%, ${P.GOLD_HI} 100%)`
                      : `${P.BORDER}55`,
                    color: accepted ? P.INK : P.MUTED,
                    border: `1px solid ${accepted ? P.GOLD : P.BORDER}88`,
                    boxShadow: accepted
                      ? `0 20px 40px -18px ${P.GOLD_DEEP}, inset 0 1px 0 ${P.GOLD_HI}88`
                      : "none",
                    cursor: accepted ? "pointer" : "not-allowed",
                    opacity: accepted ? 1 : 0.6,
                  }}
                >
                  <Trophy size={16} />
                  <span>Activate My Founder Node</span>
                  <ArrowRight size={16} />
                </button>

                <div
                  className="text-[10px] uppercase tracking-[0.24em] font-mono flex items-center gap-2"
                  style={{ color: accepted ? P.GOLD_DEEP : P.MUTED }}
                >
                  <ShieldCheck className="w-3.5 h-3.5" />
                  {accepted
                    ? "Handshake signed — ready to register"
                    : "Sign the handshake to activate"}
                </div>
              </div>

              {/* Legacy showReceipt block removed — completion now
                  transitions the whole page to <SuccessView />. */}

              <div
                className="mt-5 text-[11px] leading-[1.6] flex items-start gap-2"
                style={{ color: P.MUTED }}
              >
                <Lock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: P.GOLD_DEEP }} />
                <span>
                  Secure Stripe checkout · your card is not charged today.
                  You lock the $297 founder rate for life; Month 2 billing
                  begins only after activation. Cancel any time.
                </span>
              </div>
            </div>
          </div>

          {/* Back nav */}
          <div className="mt-10">
            <Link
              to="/"
              data-testid="ind-back-home"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-mono transition-opacity hover:opacity-70"
              style={{ color: P.MUTED }}
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to the Foyer
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// ================================================================
// SuccessView — post-activation confirmation panel.
// Fills the whole viewport with the same warm parchment aesthetic,
// centered. Deliberately no auto-redirect — user commits to the
// next step by clicking ENTER YOUR COMMAND CENTER, which sends them
// back to the vertical they came from (or home if no ?vertical param).
// ================================================================
function SuccessView({ P, returnPath, vertical }) {
  const commandCenterLabel = vertical
    ? `Enter Your Command Center · /${vertical}`
    : "Enter Your Command Center";

  return (
    <div
      className="min-h-screen relative flex items-center justify-center px-6 py-16"
      style={{
        background: P.BG,
        color: P.INK,
        backgroundImage: `
          radial-gradient(circle at 15% 20%, ${P.GOLD}0F 0.5px, transparent 1.2px),
          radial-gradient(circle at 75% 50%, ${P.LEATHER}0D 0.5px, transparent 1.2px),
          repeating-linear-gradient(115deg, transparent 0 22px, ${P.BORDER}09 22px 23px)
        `,
        backgroundSize: "34px 34px, 46px 46px, 100% 100%",
      }}
      data-testid="ind-success-view"
    >
      <div
        className="relative w-full max-w-2xl rounded-sm border-2 p-10 sm:p-14 text-center"
        style={{
          background: `linear-gradient(180deg, ${P.BG_HI} 0%, ${P.BG_DARK} 100%)`,
          borderColor: P.GOLD,
          boxShadow: `0 40px 80px -30px ${P.LEATHER}88, inset 0 1px 0 ${P.GOLD_HI}55, 0 0 0 6px ${P.GOLD}18`,
        }}
      >
        {/* Wax-seal style checkmark medallion */}
        <div
          className="mx-auto mb-8 w-20 h-20 rounded-full grid place-items-center"
          style={{
            background: `radial-gradient(circle at 30% 30%, ${P.GOLD_HI} 0%, ${P.GOLD} 55%, ${P.GOLD_DEEP} 100%)`,
            boxShadow: `0 12px 28px -8px ${P.GOLD_DEEP}, inset 0 2px 4px ${P.GOLD_HI}`,
          }}
          data-testid="ind-success-seal"
        >
          <CheckCircle2
            className="w-10 h-10"
            style={{ color: P.BG }}
            strokeWidth={2.2}
          />
        </div>

        {/* Deed-style kicker */}
        <div
          className="text-[10px] uppercase tracking-[0.32em] font-mono mb-4"
          style={{ color: P.GOLD_DEEP }}
        >
          Founder Node · Activated
        </div>

        {/* Heading */}
        <h1
          className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-5 uppercase tracking-tight"
          style={{ color: P.INK }}
          data-testid="ind-success-heading"
        >
          Connection Successful.
        </h1>

        {/* Subtext */}
        <p
          className="font-serif text-lg sm:text-xl leading-[1.55] mb-10"
          style={{ color: P.INK_SOFT }}
          data-testid="ind-success-subtext"
        >
          Your Founder Node is active. Amanda is ready.
        </p>

        {/* Command Center CTA */}
        <Link
          to={returnPath}
          data-testid="ind-command-center-cta"
          className="inline-flex items-center justify-center gap-3 px-10 py-5 rounded-sm font-display uppercase tracking-[0.24em] text-sm transition-transform hover:-translate-y-0.5"
          style={{
            background: `linear-gradient(90deg, ${P.GOLD_DEEP} 0%, ${P.GOLD_HI} 100%)`,
            color: P.INK,
            border: `1px solid ${P.GOLD}`,
            boxShadow: `0 24px 48px -20px ${P.GOLD_DEEP}, inset 0 1px 0 ${P.GOLD_HI}88`,
          }}
        >
          <Trophy size={16} />
          <span>Enter Your Command Center</span>
          <ArrowRight size={16} />
        </Link>

        <div
          className="mt-10 pt-8 border-t text-[11px] uppercase tracking-[0.24em] font-mono flex items-center justify-center gap-2"
          style={{
            borderColor: `${P.BORDER}66`,
            color: P.MUTED,
          }}
        >
          <ShieldCheck className="w-3.5 h-3.5" style={{ color: P.GOLD_DEEP }} />
          <span>{commandCenterLabel}</span>
        </div>
      </div>
    </div>
  );
}
